import {
  ENoticeDraft,
  EOrganization,
  ESnapshotExists,
  TemplateSpecs
} from 'lib/types';
import { useEffect, useState } from 'react';
import { useAppSelector } from 'redux/hooks';
import {
  selectAdTemplate,
  selectColumnCount,
  selectIsDisplayNoticeType
} from 'redux/placement';
import { getTemplateSpecs } from 'components/noticePreview/indesign_client_utils';
import { useFirestoreSnapshot } from 'lib/frontend/hooks/useFirestoreSnapshot';

/**
 * Custom hook that retrieves the general template specifications for a notice draft
 * based on the current publisher organization and ad template.
 */
export function useGetTemplateSpecs({
  publisherOrganization,
  draft
}: {
  publisherOrganization: ESnapshotExists<EOrganization> | undefined;
  draft: ESnapshotExists<ENoticeDraft>;
}) {
  const [templateSpecs, setTemplateSpecs] = useState<TemplateSpecs>();
  const adTemplateRef = useAppSelector(selectAdTemplate);
  const adTemplate = useFirestoreSnapshot(adTemplateRef);
  const isDisplayNoticeType = useAppSelector(selectIsDisplayNoticeType);
  const loading =
    adTemplate === undefined || publisherOrganization === undefined;
  const skip = !isDisplayNoticeType;
  const columns = useAppSelector(selectColumnCount);
  const draftData = skip ? undefined : draft.data();
  const dynamicHeader = draftData?.dynamicHeaders?.[0];
  const dynamicFooter = draftData?.dynamicFooter;

  useEffect(() => {
    if (skip) {
      setTemplateSpecs(undefined);
    }

    // A request started for older inputs must not overwrite a newer result.
    let superseded = false;

    if (!loading && !skip) {
      void (async function updateTemplateSpecs() {
        const updatedTemplateSpecs = await getTemplateSpecs(
          publisherOrganization,
          adTemplate,
          draft,
          columns
        );

        if (!superseded) {
          setTemplateSpecs(updatedTemplateSpecs);
        }
      })();
    }

    return () => {
      superseded = true;
    };
  }, [adTemplateRef?.id, loading, skip, columns, dynamicHeader, dynamicFooter]);

  return { templateSpecs };
}
